import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { preferencesActions, selectSelectedCurrency } from '@/entities/preferences';
import { selectWallet } from '@/entities/user';
import GiftIcon from '@/shared/assets/icons/gift.svg?react';
import StarIcon from '@/shared/assets/icons/star.svg?react';
import TonIcon from '@/shared/assets/icons/ton.svg?react';
import UploadIcon from '@/shared/assets/icons/upload.svg?react';
import { type CurrencyCode, formatCompact } from '@/shared/lib/currency';
import type { DropdownAction, DropdownItem } from '@/shared/ui/Dropdown';

const { setSelectedCurrency } = preferencesActions;

function buildSublabel(balance: number, bonus: number) {
  if (bonus <= 0) return formatCompact(balance);
  return (
    <>
      <span>{formatCompact(balance)}</span>
      <span className="dropdown__bonus">+{formatCompact(bonus)} bonus</span>
    </>
  );
}

export function useWalletDropdown(onClose: () => void, onShowWithdraw: () => void) {
  const dispatch = useAppDispatch();
  const tonWallet = useAppSelector(selectWallet('TON'));
  const starsWallet = useAppSelector(selectWallet('STARS'));
  const giftWallet = useAppSelector(selectWallet('GIFT'));
  const selectedCurrency = useAppSelector(selectSelectedCurrency);

  const handleSelect = (currency: CurrencyCode) => {
    dispatch(setSelectedCurrency(currency));
    onClose();
  };

  const giftCount = Math.round(giftWallet?.balance ?? 0);

  const items: DropdownItem[] = [
    {
      id: 'ton',
      icon: <TonIcon style={{ width: 20, height: 20, color: '#0098ea' }} />,
      label: 'TON',
      sublabel: buildSublabel(tonWallet?.balance ?? 0, tonWallet?.lockedBonus ?? 0),
      selected: selectedCurrency === 'TON',
      onClick: () => handleSelect('TON'),
    },
    {
      id: 'stars',
      icon: <StarIcon style={{ width: 20, height: 20, color: '#fbbf24' }} />,
      label: 'Stars',
      sublabel: buildSublabel(starsWallet?.balance ?? 0, starsWallet?.lockedBonus ?? 0),
      selected: selectedCurrency === 'STARS',
      onClick: () => handleSelect('STARS'),
    },
    {
      id: 'gifts',
      icon: <GiftIcon style={{ width: 20, height: 20, color: '#a855f7' }} />,
      label: 'Gifts',
      sublabel: `${giftCount} item${giftCount !== 1 ? 's' : ''}`,
      selected: false,
      onClick: () => handleSelect('GIFT'),
    },
  ];

  const actions: DropdownAction[] = [
    {
      icon: <UploadIcon style={{ width: 20, height: 20 }} />,
      label: 'Withdraw',
      onClick: onShowWithdraw,
    },
  ];

  return { items, actions };
}
